import type { ComponentType } from '@grafikr/components';
import type { Product } from '@grafikr/shopify-typescript/type/liquid/Product';
import type { ProductMedia as Media } from '@grafikr/shopify-typescript/type/liquid/ProductMedia';

import clear from 'utils/clear';
import { image as renderImage } from 'utils/image';

const component: ComponentType = (node, { emitter }) => {
  const labels: HTMLElement = node.querySelector('[data-sticky-label]');
  const form = document.getElementById('product-info');

  if (labels && window.matchMedia('(min-width: 1024px)').matches) {
    labels.style.height = `${form.offsetHeight}px`;

    const kiwi = document.getElementById('KiwiSizingChart');

    if (kiwi) {
      const observer = new MutationObserver((mutationList, obs) => {
        mutationList.forEach((mutation) => {
          if (
            mutation.addedNodes.length &&
            (mutation.addedNodes[0] as HTMLElement).classList?.contains(
              'ks-calculator-page-basic-info',
            )
          ) {
            labels.style.height = `${form.offsetHeight}px`;
            obs.disconnect();
          }
        });
      });

      observer.observe(kiwi, { subtree: true, childList: true });
    }
  }

  const lightbox: HTMLElement = node.querySelector('[data-lightbox]');

  const initSlider = async () => {
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      const { default: Swiper, Scrollbar } = await import(
        /* webpackChunkName: "pdp-images-swiper" */ 'modules/swiper'
      );

      const slider = new Swiper(node.querySelector<HTMLElement>('[data-carousel]'), {
        modules: [Scrollbar],
        slidesPerView: 'auto',
        cssMode: true,
        init: false,
        scrollbar: {
          el: '[data-thumbnails-scrollbar]',
        },
      });

      const nextArrow = node.querySelector('[data-next-arrow]');

      if (nextArrow) {
        nextArrow.addEventListener('click', () => slider.slideNext());
        slider.on('sliderFirstMove', () => nextArrow.remove());
      }

      slider.init();
    }
  };

  const init = () => {
    lightbox.addEventListener('open', () => {
      node.style.zIndex = '61';
      node.classList.remove('transform-gpu');
    });

    lightbox.addEventListener('close', () => {
      node.classList.add('transform-gpu');
      node.style.zIndex = null;
    });

    initSlider();
  };

  init();

  emitter.on('product:media:updated', ({ media }: { media: Media[]; product: Product }) => {
    const lightBox = node.querySelector('[data-lb-images]');
    const imageWrapper = node.querySelector('[data-images]');

    clear(imageWrapper);
    imageWrapper.innerHTML = media
      .map((item, index) => {
        let orderIndex: number;
        switch (index) {
          case 0:
            orderIndex = 1;
            break;
          case 1:
            orderIndex = 2;
            break;
          case 2:
            orderIndex = 0;
            break;
          default:
            orderIndex = index;
            break;
        }

        return `<div class="swiper-slide w-full shrink-0 snap-start snap-always lg:h-auto lg:order-[var(--order)] ${
          media.length === 1 ? '' : 'lg:w-[calc(50%-0.125rem)]'
        }" data-image="${index}" style="--order: ${orderIndex}">
      <div class="aspect-w-3 aspect-h-4">
      ${renderImage(item)}
      </div>
      </div>`;
      })
      .join('');

    clear(lightBox);
    lightBox.innerHTML = media
      .map(
        (item) => `<div
          data-lb-image
          class="aspect-w-1"
          style="--tw-aspect-h: ${1 / item.aspect_ratio}"
        >
        ${renderImage(item)}
        </div>`,
      )
      .join('');

    init();
  });
};

export default component;
